/**
 * Site-wide search dropdown — same shape as MenuSearchDropdown but with a
 * Strains section and Link-free row handlers (parent navigates via the
 * activateRow callback).
 *
 * `inline` prop hides the absolute-positioning so the parent can place
 * this anywhere (used for mobile full-screen overlay).
 */
import { Search, Clock, Tag, Leaf } from "lucide-react";

export default function GlobalSearchDropdown({
    q,
    value,
    history,
    rows,
    highlight,
    strainHits,
    brandHits,
    productHits,
    storeSlug,
    onClearHistory,
    onActivateRow,
    onCommit,
    inline = false,
}) {
    const wrapClass = inline
        ? "bg-white border border-zen-ink/15 shadow-[0_12px_36px_rgba(31,36,24,0.12)] max-h-[70vh] overflow-y-auto"
        : "absolute left-0 right-0 top-full mt-1 bg-white border border-zen-ink/15 shadow-[0_12px_36px_rgba(31,36,24,0.12)] z-50 max-h-[70vh] overflow-y-auto";

    return (
        <div data-testid="global-search-dropdown" className={wrapClass}>
            {/* RECENT */}
            {!q && history.length > 0 && (
                <div data-testid="search-section-recent">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-zen-line">
                        <p className="text-[10px] uppercase tracking-[0.28em] text-zen-olive">
                            Recent searches
                        </p>
                        <button
                            onClick={onClearHistory}
                            data-testid="search-clear-history"
                            className="text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft hover:text-zen-ink"
                        >
                            Clear
                        </button>
                    </div>
                    {history.map((h, i) => {
                        const idx = rows.findIndex(
                            (r) => r.type === "history" && r.value === h
                        );
                        const active = idx === highlight;
                        return (
                            <button
                                key={h}
                                onClick={() => onActivateRow(rows[idx])}
                                data-testid={`search-history-${i}`}
                                className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                                    active
                                        ? "bg-zen-olive/10 text-zen-ink"
                                        : "text-zen-ink-soft hover:bg-zen-surface hover:text-zen-ink"
                                }`}
                            >
                                <Clock size={12} className="text-zen-olive" />
                                <span className="flex-1 truncate">{h}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* STRAINS */}
            {q && strainHits.length > 0 && (
                <div data-testid="search-section-strains">
                    <div className="px-3 py-2 border-b border-zen-line">
                        <p className="text-[10px] uppercase tracking-[0.28em] text-zen-olive">
                            Strains
                        </p>
                    </div>
                    {strainHits.map((s) => {
                        const idx = rows.findIndex(
                            (r) => r.type === "strain" && r.value.slug === s.slug
                        );
                        const active = idx === highlight;
                        return (
                            <button
                                key={s.slug}
                                onClick={() => onActivateRow(rows[idx])}
                                data-testid={`search-strain-${s.slug}`}
                                className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                                    active
                                        ? "bg-zen-olive/10 text-zen-ink"
                                        : "text-zen-ink-soft hover:bg-zen-surface hover:text-zen-ink"
                                }`}
                            >
                                <Leaf size={12} className="text-zen-olive" />
                                <span className="flex-1 truncate font-medium text-zen-ink">
                                    {s.name}
                                </span>
                                {s.strain_type && (
                                    <span className="text-[10px] uppercase tracking-wider text-zen-olive bg-zen-olive/10 px-1.5 py-0.5">
                                        {s.strain_type.replace("_", "-")}
                                    </span>
                                )}
                                <span className="text-[10px] text-zen-muted tabular-nums">
                                    {s.product_count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* BRANDS */}
            {q && brandHits.length > 0 && (
                <div data-testid="search-section-brands">
                    <div className="px-3 py-2 border-b border-zen-line">
                        <p className="text-[10px] uppercase tracking-[0.28em] text-zen-olive">
                            Brands
                        </p>
                    </div>
                    {brandHits.map((b) => {
                        const idx = rows.findIndex(
                            (r) => r.type === "brand" && r.value.name === b.name
                        );
                        const active = idx === highlight;
                        return (
                            <button
                                key={b.name}
                                onClick={() => onActivateRow(rows[idx])}
                                data-testid={`search-brand-${b.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                                    active
                                        ? "bg-zen-olive/10 text-zen-ink"
                                        : "text-zen-ink-soft hover:bg-zen-surface hover:text-zen-ink"
                                }`}
                            >
                                <Tag size={12} className="text-zen-olive" />
                                <span className="flex-1 truncate font-medium text-zen-ink">
                                    {b.name}
                                </span>
                                <span className="text-[10px] text-zen-muted tabular-nums">
                                    {b.count} item{b.count !== 1 ? "s" : ""}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* PRODUCTS */}
            {q && productHits.length > 0 && (
                <div data-testid="search-section-products">
                    <div className="px-3 py-2 border-b border-zen-line">
                        <p className="text-[10px] uppercase tracking-[0.28em] text-zen-olive">
                            Products
                        </p>
                    </div>
                    {productHits.map((p) => {
                        const idx = rows.findIndex(
                            (r) =>
                                r.type === "product" &&
                                (r.value.id === p.id || r.value.slug === p.slug)
                        );
                        const active = idx === highlight;
                        const v0 =
                            (p.variants || []).find((v) => (v.quantity || 0) > 0) ||
                            (p.variants || [])[0];
                        const price = v0?.specialPriceRec || v0?.priceRec;
                        const img = ((p.images || [])[0] || {}).url || "";
                        return (
                            <button
                                key={p.id || p.slug}
                                onClick={() => onActivateRow(rows[idx])}
                                data-testid={`search-product-${p.slug || p.id}`}
                                className={`w-full text-left flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                    active ? "bg-zen-olive/10" : "hover:bg-zen-surface"
                                }`}
                            >
                                <div className="w-10 h-10 bg-zen-bg border border-zen-line overflow-hidden shrink-0">
                                    {img ? (
                                        <img
                                            src={img}
                                            alt=""
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : null}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-zen-ink leading-tight truncate">
                                        {p.name}
                                    </p>
                                    <p className="text-[11px] text-zen-ink-soft truncate">
                                        {p?.brand?.name || ""}
                                        {v0?.option ? ` · ${v0.option}` : ""}
                                    </p>
                                </div>
                                {price ? (
                                    <span className="text-xs text-zen-ink tabular-nums whitespace-nowrap">
                                        ${Number(price).toFixed(2)}
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* COMMIT */}
            {q && (
                <button
                    onClick={() => onCommit(value)}
                    data-testid="search-commit"
                    className={`w-full text-left flex items-center gap-2 px-3 py-3 text-sm border-t border-zen-line transition-colors ${
                        highlight === rows.length - 1
                            ? "bg-zen-olive/15 text-zen-ink"
                            : "text-zen-ink-soft hover:bg-zen-surface hover:text-zen-ink"
                    }`}
                >
                    <Search size={12} className="text-zen-olive" />
                    <span>
                        See all results for{" "}
                        <span className="font-semibold text-zen-ink">
                            “{value.trim()}”
                        </span>
                    </span>
                </button>
            )}

            {q &&
                strainHits.length === 0 &&
                brandHits.length === 0 &&
                productHits.length === 0 && (
                    <div className="px-3 py-4 text-sm text-zen-ink-soft">
                        No matches in the live menu.{" "}
                        <span className="text-zen-ink">Press Enter</span> to search
                        anyway.
                    </div>
                )}
        </div>
    );
}
